export const TOKEN_ID_COOKIE = 'token_id';

/**
 * Generate a cookie wth the provided refresh JWT or other value like token_id
 * @param value JWT or other cookie value to set in the cookie
 * @param domain optional domain, defaults to process.env.DOMAIN
 * @param path optional path attribute. Defaults to auto generated by browser
 * @param key cookie key, defaults to 'refresh'
 * @param maxAge cookie max age in seconds, defaults to 3600 (1 hour)
 * @param httpOnly cookie httpOnly attribute, defaults to true
 * @returns string used in set-cookie header
 */
export const createCookie = (
  value: string,
  domain?: string,
  path?: string,
  key = 'refresh',
  maxAge = 3600,
  httpOnly = true,
): string => {
  const domainValue = domain ?? process.env.DOMAIN;
  const pathAttribute = path ? ` Path=${path};` : '';
  const httpOnlyAttribute = httpOnly ? ' HttpOnly;' : '';

  return `${key}=${value}; Domain=${domainValue};${pathAttribute} Secure;${httpOnlyAttribute} Max-Age=${maxAge};`;
};

/**
 * Delete a cookie helper by setting the max age to 0, other parameters should
 * match those used when creating the cookie to ensure it is deleted properly
 * @param value JWT or other cookie value to set in the cookie
 * @param domain optional domain, defaults to process.env.DOMAIN
 * @param key cookie key, defaults to 'refresh'
 * @param httpOnly cookie httpOnly attribute, defaults to true
 */
export const deleteCookie = (
  value: string,
  domain: string,
  key: string,
  httpOnly = true,
): string => {
  return createCookie(value, domain, undefined, key, 0, httpOnly);
};

/**
 * Create a specific tokenId cookie with 2 hour expiry
 * @param tokenId The token id to set in the cookie
 * @param domain optional domain
 */
export const createTokenIdCookie = (
  tokenId: string,
  domain?: string,
): string => {
  // Local test cookie
  if (process.env.IS_OFFLINE) {
    return `${TOKEN_ID_COOKIE}=${tokenId}; Domain=${
      domain ?? 'auth.carafe.dev'
    }; Max-Age=7200; Port=3000; Path=/; Secure; HttpOnly;`;
  }
  // The Apple SSO login requires the path to be set to '/' for the cookie to be sent
  return createCookie(tokenId, domain, '/', 'token_id', 7200);
};

/**
 * Delete the tokenId cookie by setting a zero max age
 * @param tokenId The token id to set in the cookie to delete
 * @param domain optional domain
 */
export const deleteTokenIdCookie = (
  tokenId: string,
  domain?: string,
): string => {
  // Local test cookie
  if (process.env.IS_OFFLINE) {
    return `${TOKEN_ID_COOKIE}=${tokenId}; Domain=${
      domain ?? 'auth.carafe.dev'
    }; Max-Age=0; Port=3000; Path=/; Secure; HttpOnly;`;
  }

  // The Apple SSO login requires the path to be set to '/' for the cookie to be created
  // So need the same path for the cookie to be deleted
  return createCookie(tokenId, domain, '/', 'token_id', 0);
};

/**
 * Check the cookie header for the presence of the token id cookie
 * @param cookieHeader
 */
export const hasTokenIdCookie = (cookieHeader?: string): boolean => {
  return !!cookieHeader && cookieHeader.includes(`${TOKEN_ID_COOKIE}=`);
};
